import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #lastChangeWasColor = false;

  /** @type {boolean} */
  #shouldAutoSelectSizeAfterUpdate = false;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();

    this.#rebuildRadiosCache();
    this.sortSizeOptions();
    // Sorting can change DOM order; keep caches in sync.
    this.#rebuildRadiosCache();

    this.updateVariantTitle();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.#lastChangeWasColor = Boolean(event.target.closest('fieldset')?.dataset.colorOption === 'true');

    // If the user clicked a color thumbnail, auto-select the smallest size (mbi-tuin only)
    // so the request resolves a full variant and the price updates immediately.
    if (this.#lastChangeWasColor && this.dataset.autoSelectSmallestSize === 'true') {
      this.#shouldAutoSelectSizeAfterUpdate = true;
      this.sortSizeOptions();
      this.#rebuildRadiosCache();
      this.selectSmallestAvailableSize();
    }

    this.updateSelectedOption(event.target);

    // Update the title immediately on any selection.
    this.updateVariantTitle();
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');

      if (!Number.isNaN(fieldsetIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        let radios = this.#radios[fieldsetIndex];

        // DOM order might have changed (e.g., we sort size buttons). Resolve index by identity.
        let inputIndex = radios ? radios.indexOf(target) : -1;
        if (inputIndex === -1) {
          this.#rebuildRadiosCache();
          radios = this.#radios[fieldsetIndex];
          inputIndex = radios ? radios.indexOf(target) : -1;
        }

        if (inputIndex === -1) return;

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If we already know an exact variant, include it so server-rendered sections
    // (e.g. media gallery) can resolve the selected variant immediately.
    if (selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    }

    // MBI-tuin collection card: preserve thumbnail swatches + extra card info during section rendering.
    // The Section Rendering API request is to the product URL, so we can't rely on Liquid's template suffix.
    // Instead, we pass an explicit flag when the picker is inside an MBI-tuin collection product-card.
    if (this.dataset.mbiTuinCard === '1') {
      params.push('mbi_tuin_card=1');
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // MBI-tuin: also update the specs dropdown under "Gegevens".
          if (this.dataset.templateSuffix === 'mbi-tuin') {
            this.updateMbiTuinSpecs(html);
            this.updateMbiTuinStars(html);
          }

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  updateMbiTuinSpecs(newHtml) {
    const current = document.querySelector('[data-key="mbi-tuin-specs"]');
    const next = newHtml.querySelector('[data-key="mbi-tuin-specs"]');

    if (!(current instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    morph(current, next, MORPH_OPTIONS);
  }

  updateMbiTuinStars(newHtml) {
    const current = document.querySelector('[data-key="mbi-tuin-stars"]');
    const next = newHtml.querySelector('[data-key="mbi-tuin-stars"]');

    if (!(current instanceof HTMLElement) || !(next instanceof HTMLElement)) return;

    morph(current, next, MORPH_OPTIONS);
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // Keep Afmeting options ordered (smallest first) after re-render.
    this.sortSizeOptions();
    // After morphing (and sorting), inputs/fieldsets might have been replaced; rebuild caches.
    this.#rebuildRadiosCache();

    // Keep title in sync after re-render.
    this.updateVariantTitle();

    // If the user just changed a color, the server response might reset/disable size selections
    // depending on availability. In that case, pick the smallest AVAILABLE size and trigger a
    // follow-up change so the price resolves.
    if (this.#shouldAutoSelectSizeAfterUpdate && this.dataset.autoSelectSmallestSize === 'true') {
      this.#shouldAutoSelectSizeAfterUpdate = false;
      const didSelect = this.selectSmallestAvailableSize({ dispatchChange: true, allowReplaceUnavailable: true });
      if (didSelect) {
        // No further work needed here; the change event will fetch the correct sections.
      }
    }

    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Update the product title line to include selected color + size.
   * Expects markup like:
   *   <h4 data-variant-title data-base-title="...">
   *     <span data-variant-title-base>...</span>
   *     <span data-variant-title-color></span>
   *     <span data-variant-title-size></span>
   *   </h4>
   */
  updateVariantTitle() {
    const titleEl = this.closest('section')?.querySelector('[data-variant-title]') ||
      document.querySelector('[data-variant-title]');

    if (!(titleEl instanceof HTMLElement)) return;

    const baseTitle = titleEl.dataset.baseTitle || titleEl.querySelector('[data-variant-title-base]')?.textContent || '';

    const colorValue =
      this.querySelector('fieldset[data-color-option="true"] input:checked')?.value?.trim() || '';
    const sizeValue =
      this.querySelector('fieldset[data-size-option="true"] input:checked')?.value?.trim() || '';

    const baseSpan = titleEl.querySelector('[data-variant-title-base]');
    const colorSpan = titleEl.querySelector('[data-variant-title-color]');
    const sizeSpan = titleEl.querySelector('[data-variant-title-size]');

    if (baseSpan) baseSpan.textContent = baseTitle;
    if (colorSpan) colorSpan.textContent = colorValue ? ` ${colorValue}` : '';
    if (sizeSpan) sizeSpan.textContent = sizeValue ? ` ${sizeValue}` : '';

    // Fallback if spans are removed by editor.
    if (!baseSpan || !colorSpan || !sizeSpan) {
      titleEl.textContent = [baseTitle, colorValue, sizeValue].filter(Boolean).join(' ');
    }
  }

  #rebuildRadiosCache() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
      this.#checkedIndices[fieldsetIndex] = [];

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices[fieldsetIndex].push(initialCheckedIndex);
      }
    });
  }

  /**
   * Sort the buttons for size options (Afmeting) so the smallest is first.
   */
  sortSizeOptions() {
    /** @type {HTMLFieldSetElement[]} */
    const sizeFieldsets = Array.from(this.querySelectorAll('fieldset[data-size-option="true"]'));
    if (!sizeFieldsets.length) return;

    for (const fieldset of sizeFieldsets) {
      const labels = Array.from(fieldset.querySelectorAll(':scope > label.variant-option__button-label'));
      if (labels.length < 2) continue;

      const styleTag = fieldset.querySelector(':scope > style');

      const parsed = labels.map((label, originalIndex) => {
        const input = label.querySelector('input');
        const raw =
          (input instanceof HTMLInputElement && (input.value || input.getAttribute('value'))) ||
          label.textContent ||
          '';

        const nums = Array.from(raw.replace(/,/g, '.').matchAll(/\d+(?:\.\d+)?/g)).map((m) =>
          Number.parseFloat(m[0])
        );

        return { label, originalIndex, raw: raw.trim(), nums };
      });

      parsed.sort((a, b) => {
        const aNums = a.nums;
        const bNums = b.nums;

        if (aNums.length && bNums.length) {
          const len = Math.max(aNums.length, bNums.length);
          for (let i = 0; i < len; i++) {
            const av = aNums[i] ?? Number.POSITIVE_INFINITY;
            const bv = bNums[i] ?? Number.POSITIVE_INFINITY;
            if (av !== bv) return av - bv;
          }
          return 0;
        }

        if (aNums.length) return -1;
        if (bNums.length) return 1;
        return a.raw.localeCompare(b.raw, undefined, { numeric: true, sensitivity: 'base' });
      });

      const frag = document.createDocumentFragment();
      for (const item of parsed) frag.appendChild(item.label);

      if (styleTag) {
        fieldset.insertBefore(frag, styleTag);
      } else {
        fieldset.appendChild(frag);
      }
    }
  }

  /**
   * Select the smallest AVAILABLE size option.
   * Only acts if there is a size fieldset and a valid candidate.
   */
  selectSmallestAvailableSize() {
    return this.selectSmallestAvailableSize({ dispatchChange: false, allowReplaceUnavailable: false });
  }

  /**
   * Select the smallest AVAILABLE size option.
   * @param {{ dispatchChange?: boolean; allowReplaceUnavailable?: boolean }} [options]
   * @returns {boolean} True if a new selection was made.
   */
  selectSmallestAvailableSize(options) {
    const { dispatchChange = false, allowReplaceUnavailable = false } = options || {};

    /** @type {HTMLFieldSetElement | null} */
    const sizeFieldset = this.querySelector('fieldset[data-size-option="true"]');
    if (!sizeFieldset) return false;

    const isSelectable = (input) => {
      if (!(input instanceof HTMLInputElement)) return false;
      if (input.getAttribute('aria-disabled') === 'true') return false;
      if (input.dataset.optionAvailable === 'false') return false;
      return true;
    };

    const alreadyChecked = sizeFieldset.querySelector('input:checked');
    if (alreadyChecked instanceof HTMLInputElement) {
      if (!allowReplaceUnavailable || isSelectable(alreadyChecked)) return false;
    }

    const labels = Array.from(sizeFieldset.querySelectorAll(':scope > label.variant-option__button-label'));
    for (const label of labels) {
      const input = label.querySelector('input');
      if (!isSelectable(input)) continue;

      this.updateSelectedOption(/** @type {HTMLInputElement} */ (input));

      if (dispatchChange) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return true;
    }

    return false;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
